import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  HandshakeIcon, UsersThree, PlusCircle, Crown, FilePdf, EnvelopeSimple,
  CheckCircle, X, Coins, ArrowLeft, FileText, PaperPlaneTilt, BookOpen, Question, ListChecks, ArrowRight, Target,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_BADGE = {
  draft: "bg-zinc-800 text-zinc-300",
  submitted: "bg-blue-500/15 text-blue-300 border border-blue-500/30",
  awarded: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  closed: "bg-zinc-700 text-zinc-400",
};

const INVITE_BADGE = {
  pending: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  accepted: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  declined: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
};

const Coalitions = () => {
  const { coalitionId } = useParams();
  return coalitionId ? <CoalitionDetail coalitionId={coalitionId} /> : <CoalitionList />;
};

const CoalitionList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const grantIdFilter = searchParams.get("grant_id") || "";
  const [coalitions, setCoalitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showFirstTime, setShowFirstTime] = useState(false);
  const [form, setForm] = useState({ name: "", project_summary: "", target_grant: "", total_budget: "", shared_outcome: "", shared_population: "", shared_deliverable: "" });
  const [creating, setCreating] = useState(false);
  const [grantContext, setGrantContext] = useState(null); // { title, funder_name } — for the pre-fill banner

  // If ?grant_id=X was passed (via the Partnership → Coalition widget), fetch the
  // grant metadata, pre-fill the target_grant field, and auto-open the create dialog.
  useEffect(() => {
    if (!grantIdFilter) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/grants/${grantIdFilter}`);
        if (cancelled) return;
        const g = res.data;
        setGrantContext({ title: g.title, funder_name: g.funder_name });
        setForm((prev) => ({
          ...prev,
          target_grant: g.title || prev.target_grant,
          name: prev.name || `Coalition for ${g.title || "this grant"}`,
        }));
        // Auto-open the create dialog (skip first-time modal since user came from a directed CTA)
        localStorage.setItem("coalition_intro_seen", "1");
        setShowCreate(true);
      } catch { /* silent — banner just won't show pre-filled title */ }
    })();
    return () => { cancelled = true; };
  }, [grantIdFilter]);

  const clearGrantFilter = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("grant_id");
    setSearchParams(p);
    setGrantContext(null);
  };

  const openCreate = () => {
    // Show the first-time walkthrough exactly once per browser
    if (!localStorage.getItem("coalition_intro_seen")) {
      setShowFirstTime(true);
    } else {
      setShowCreate(true);
    }
  };

  const dismissFirstTime = () => {
    localStorage.setItem("coalition_intro_seen", "1");
    setShowFirstTime(false);
    setShowCreate(true);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coalitions/mine`);
      setCoalitions(res.data.coalitions || []);
    } catch {
      toast.error("Couldn't load your coalitions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCoalition = async () => {
    if (!form.name.trim()) return toast.error("Coalition name is required");
    if (form.shared_outcome.trim().length < 15) {
      return toast.error("Shared Outcome: write one sentence (15+ chars) on what the coalition is trying to accomplish.");
    }
    if (form.shared_population.trim().length < 5) {
      return toast.error("Population Served is required — who specifically does the coalition serve?");
    }
    if (form.shared_deliverable.trim().length < 10) {
      return toast.error("Deliverable is required — what will exist at the end of the project?");
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/coalitions`, {
        name: form.name,
        project_summary: form.project_summary,
        target_grant: form.target_grant,
        total_budget: parseFloat(form.total_budget) || 0,
        shared_outcome: form.shared_outcome,
        shared_population: form.shared_population,
        shared_deliverable: form.shared_deliverable,
      });
      toast.success("Coalition created. Now invite your partners.");
      setShowCreate(false);
      setForm({ name: "", project_summary: "", target_grant: "", total_budget: "", shared_outcome: "", shared_population: "", shared_deliverable: "" });
      navigate(`/coalitions/${res.data.coalition_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create coalition");
    } finally {
      setCreating(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-5 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <HandshakeIcon weight="duotone" className="w-7 h-7 text-emerald-400" />
              Coalitions
            </h1>
            <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
              Apply for grants <em>together</em>. One org is the Lead Applicant — they submit, receive funds, and distribute to partners per a signed agreement. FlashGrants handles the paperwork.
              <br />
              <span className="text-xs text-zinc-500">
                Need to find partners first?{" "}
                <Link to="/collaborate" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 font-semibold" data-testid="link-to-find-partners">Find Partners →</Link>
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/coalitions/playbook">
              <Button variant="ghost" className="text-zinc-300 border border-zinc-800 hover:border-emerald-500/50" data-testid="playbook-link">
                <BookOpen weight="fill" className="w-4 h-4 mr-2 text-emerald-400" />
                Playbook
              </Button>
            </Link>
            <Button
              onClick={openCreate}
              className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold"
              data-testid="create-coalition-btn"
            >
              <PlusCircle weight="bold" className="w-4 h-4 mr-2" />
              New Coalition
            </Button>
          </div>
        </div>

        {grantContext && (
          <Card className="bg-emerald-500/5 border border-emerald-500/40" data-testid="coalition-grant-context-banner">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Target weight="fill" className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">
                    Starting a coalition for one grant
                  </p>
                </div>
                <p className="text-sm text-white truncate" data-testid="coalition-grant-title">
                  <span className="font-bold">{grantContext.title}</span>
                  {grantContext.funder_name && <span className="text-zinc-400"> · {grantContext.funder_name}</span>}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  We've pre-filled the Target Grant field. Add partners as members once created.
                </p>
              </div>
              <Button
                onClick={clearGrantFilter}
                variant="ghost"
                className="text-zinc-400 hover:text-white rounded-sm text-xs"
                data-testid="clear-coalition-grant-filter-btn"
              >
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <p className="text-zinc-500 text-sm">Loading…</p>
        ) : coalitions.length === 0 ? (
          <Card className="bg-zinc-950 border-zinc-800 border-dashed">
            <CardContent className="py-14 text-center" data-testid="coalitions-empty">
              <UsersThree weight="duotone" className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-300 font-semibold mb-1">No coalitions yet.</p>
              <p className="text-zinc-500 text-sm max-w-md mx-auto mb-5">
                Coalitions let 2–6 SC nonprofits apply for one grant together. Higher win rate, bigger asks, shared work.
              </p>
              <Button onClick={openCreate} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold">
                <PlusCircle weight="bold" className="w-4 h-4 mr-2" />Start your first coalition
              </Button>
              <p className="text-xs text-zinc-500 mt-4">
                Or{" "}
                <Link to="/collaborate" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 font-semibold">find partner organizations first</Link>
                .
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {coalitions.map(c => (
              <Link key={c.coalition_id} to={`/coalitions/${c.coalition_id}`}>
                <Card className="bg-zinc-950 border-zinc-800 hover:border-emerald-500/50 transition-colors cursor-pointer h-full" data-testid={`coalition-card-${c.coalition_id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-white line-clamp-2">{c.name}</h3>
                      <span className={`text-[10px] px-2 py-1 rounded uppercase tracking-wider font-bold ${STATUS_BADGE[c.status] || STATUS_BADGE.draft}`}>{c.status}</span>
                    </div>
                    {c.target_grant && <p className="text-xs text-emerald-400 font-semibold mb-2">→ {c.target_grant}</p>}
                    <p className="text-zinc-400 text-sm line-clamp-2 mb-3">{c.project_summary || c.description || "No summary yet."}</p>
                    <div className="flex items-center justify-between text-xs text-zinc-500 pt-3 border-t border-zinc-800">
                      <span className="flex items-center gap-1.5">
                        <UsersThree className="w-3.5 h-3.5" />{c.members?.length || 0} members
                      </span>
                      {c.total_budget > 0 && (
                        <span className="flex items-center gap-1 font-mono">
                          <Coins className="w-3.5 h-3.5" />${c.total_budget.toLocaleString()}
                        </span>
                      )}
                      {c.is_lead && <span className="flex items-center gap-1 text-amber-400"><Crown weight="fill" className="w-3.5 h-3.5" />Lead</span>}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showFirstTime} onOpenChange={setShowFirstTime}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="first-time-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UsersThree weight="duotone" className="w-6 h-6 text-emerald-400" />
              Before you start — the 30-second version
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center font-black text-emerald-400">1</span>
              <p className="text-zinc-300"><b className="text-white">What a coalition is.</b> 2–6 SC nonprofits apply for ONE grant together. Bigger ask, higher win rate, shared work.</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center font-black text-emerald-400">2</span>
              <p className="text-zinc-300"><b className="text-white">One org is the Lead.</b> They submit the application, receive the funds, and distribute to partners per a signed agreement.</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center font-black text-emerald-400">3</span>
              <p className="text-zinc-300"><b className="text-white">Paperwork is automated.</b> FlashGrants generates the MOU + Subgrant Agreements from your coalition data. You print, all parties sign.</p>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-sm bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center font-black text-emerald-400">4</span>
              <p className="text-zinc-300"><b className="text-white">Money flows once.</b> Funder cuts ONE check to the Lead. The Lead distributes to each partner per the Subgrant Agreement.</p>
            </div>
            <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-sm p-3 mt-3">
              <p className="text-xs text-emerald-200">
                Want the full step-by-step?{" "}
                <Link to="/coalitions/playbook" className="text-emerald-400 hover:text-emerald-300 underline font-semibold" data-testid="first-time-playbook-link">Read the Playbook</Link>
                {" "}— it walks through every step with examples.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowFirstTime(false)}>Maybe later</Button>
            <Button onClick={dismissFirstTime} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="first-time-continue">
              Got it — let's build <ArrowRight weight="bold" className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Create a new coalition</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Coalition name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Pee Dee Women & Girls Joint Initiative" className="bg-zinc-900 border-zinc-800" data-testid="new-coalition-name" />
            </div>
            <div>
              <Label className="text-xs">Project summary</Label>
              <Textarea value={form.project_summary} onChange={(e) => setForm({ ...form, project_summary: e.target.value })} placeholder="What will the coalition do together?" className="bg-zinc-900 border-zinc-800 min-h-[90px]" data-testid="new-coalition-summary" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Target grant</Label>
                <Input value={form.target_grant} onChange={(e) => setForm({ ...form, target_grant: e.target.value })} placeholder="AAUW, etc." className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs">Total budget ($)</Label>
                <Input type="number" value={form.total_budget} onChange={(e) => setForm({ ...form, total_budget: e.target.value })} placeholder="35000" className="bg-zinc-900 border-zinc-800" />
              </div>
            </div>

            {/* ── Shared Outcome Diagnostic ─────────────────────────────── */}
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <div className="inline-flex items-center gap-2 px-2 py-1 rounded-sm bg-emerald-500/10 border border-emerald-500/30 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-[10px] font-bold tracking-[0.18em] text-emerald-300 uppercase">Shared Outcome Check</span>
              </div>
              <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
                Coalitions don't fail because the missions are different. They fail because partners can't articulate what they're collectively trying to do. Answer these three before inviting anyone — funders look for exactly this.
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">1. Shared Outcome — in one sentence, what is the coalition trying to accomplish? *</Label>
                  <Textarea
                    value={form.shared_outcome}
                    onChange={(e) => setForm({ ...form, shared_outcome: e.target.value })}
                    placeholder="e.g. Host a free 3-day women's leadership retreat for underserved teen girls in Horry County."
                    className="bg-zinc-900 border-zinc-800 min-h-[60px]"
                    data-testid="new-coalition-outcome"
                  />
                </div>
                <div>
                  <Label className="text-xs">2. Population Served — who specifically does it serve? *</Label>
                  <Input
                    value={form.shared_population}
                    onChange={(e) => setForm({ ...form, shared_population: e.target.value })}
                    placeholder="e.g. Teen girls (ages 13-17) in Horry County, SC"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="new-coalition-population"
                  />
                </div>
                <div>
                  <Label className="text-xs">3. Deliverable — what will exist at the end? *</Label>
                  <Input
                    value={form.shared_deliverable}
                    onChange={(e) => setForm({ ...form, shared_deliverable: e.target.value })}
                    placeholder="e.g. 1 retreat. 200 girls reached. Curriculum published."
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="new-coalition-deliverable"
                  />
                </div>
              </div>
              <p className="text-[10px] text-zinc-600 mt-2 italic">
                These answers auto-populate your MOU and grant draft sections. Be specific — funders fund deliverables, not feelings.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createCoalition} disabled={creating} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="new-coalition-submit">
              {creating ? "Creating…" : "Create Coalition"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

const CoalitionDetail = ({ coalitionId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [coalition, setCoalition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", org_name: "" });
  const [splitDraft, setSplitDraft] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/coalitions/${coalitionId}`);
      setCoalition(res.data);
      const splits = {};
      (res.data.members || []).forEach(m => {
        splits[m.user_id] = { budget_share_pct: m.budget_share_pct, scope_of_work: m.scope_of_work };
      });
      setSplitDraft(splits);
    } catch (e) {
      if (e.response?.status === 403) toast.error("You don't have access to this coalition");
      else if (e.response?.status === 404) toast.error("Coalition not found");
      navigate("/coalitions");
    } finally {
      setLoading(false);
    }
  }, [coalitionId, navigate]);

  useEffect(() => { load(); }, [load]);

  const inviteSubmit = async () => {
    if (!inviteForm.email) return toast.error("Email required");
    try {
      const res = await axios.post(`${API}/coalitions/${coalitionId}/invite`, inviteForm);
      setCoalition(res.data);
      toast.success("Invite sent");
      setShowInvite(false);
      setInviteForm({ email: "", org_name: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't invite partner");
    }
  };

  const saveSplits = async () => {
    const splits = Object.entries(splitDraft).map(([user_id, v]) => ({ user_id, ...v }));
    const total = splits.reduce((sum, s) => sum + (parseFloat(s.budget_share_pct) || 0), 0);
    if (Math.abs(total - 100) > 0.5) {
      return toast.error(`Splits must sum to 100% (currently ${total.toFixed(1)}%)`);
    }
    try {
      const res = await axios.put(`${API}/coalitions/${coalitionId}`, { splits });
      setCoalition(res.data);
      toast.success("Budget split saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save splits");
    }
  };

  const respondInvite = async (response) => {
    try {
      await axios.post(`${API}/coalitions/${coalitionId}/respond`, { response });
      toast.success(response === "accept" ? "Welcome to the coalition" : "Declined");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't respond");
    }
  };

  const openDoc = async (path) => {
    try {
      const res = await axios.get(`${API}${path}`, { responseType: "text" });
      const blob = new Blob([res.data], { type: "text/html" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) {
      toast.error("Couldn't open document");
    }
  };

  if (loading) return <DashboardLayout><div className="p-8 text-zinc-500">Loading…</div></DashboardLayout>;
  if (!coalition) return null;

  const myMembership = (coalition.members || []).find(m => m.user_id === user?.user_id || m.email?.toLowerCase() === user?.email?.toLowerCase());
  const isPending = myMembership?.invite_status === "pending";
  const isLead = coalition.is_lead;
  const acceptedPartners = (coalition.members || []).filter(m => m.role === "partner" && m.invite_status === "accepted");
  const splitTotal = Object.values(splitDraft).reduce((s, v) => s + (parseFloat(v.budget_share_pct) || 0), 0);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-5 py-8 space-y-6">
        <Link to="/coalitions" className="text-xs text-zinc-500 hover:text-emerald-400 inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All coalitions</Link>

        {isPending && (
          <Card className="bg-amber-500/5 border-amber-500/30" data-testid="invite-action-card">
            <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-amber-300 font-bold mb-1">You've been invited to this coalition</p>
                <p className="text-zinc-400 text-sm">Accept to receive a budget share, scope of work, and access to the MOU.</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => respondInvite("decline")} variant="ghost" className="text-zinc-400" data-testid="invite-decline">Decline</Button>
                <Button onClick={() => respondInvite("accept")} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="invite-accept">Accept invitation</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{coalition.name}</h1>
                {coalition.target_grant && <p className="text-emerald-400 font-semibold text-sm mt-1">→ {coalition.target_grant}</p>}
              </div>
              <span className={`text-[10px] px-2 py-1 rounded uppercase tracking-wider font-bold ${STATUS_BADGE[coalition.status] || STATUS_BADGE.draft}`}>{coalition.status}</span>
            </div>
            {coalition.project_summary && <p className="text-zinc-300 text-sm mt-3 leading-relaxed">{coalition.project_summary}</p>}
            {coalition.total_budget > 0 && (
              <p className="text-xs text-zinc-500 mt-3 font-mono">Total project budget: <span className="text-white font-bold">${coalition.total_budget.toLocaleString()}</span></p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><UsersThree weight="fill" className="w-5 h-5 text-emerald-400" />Members ({coalition.members?.length || 0})</CardTitle>
              {isLead && (coalition.members?.length || 0) < 6 && (
                <Button onClick={() => setShowInvite(true)} size="sm" className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white" data-testid="invite-partner-btn">
                  <PaperPlaneTilt className="w-3.5 h-3.5 mr-1" />Invite Partner
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(coalition.members || []).map(m => (
              <div key={m.user_id + m.email} className="flex items-center justify-between gap-3 p-3 bg-zinc-900/60 rounded-sm border border-zinc-800" data-testid={`member-row-${m.email}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-white truncate">{m.org_name}</span>
                    {m.role === "lead" && <span className="text-[10px] text-amber-400 font-bold uppercase flex items-center gap-1"><Crown weight="fill" className="w-3 h-3" />Lead</span>}
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {m.budget_share_pct > 0 && <span className="text-xs font-mono text-emerald-400">{m.budget_share_pct.toFixed(0)}%</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider ${INVITE_BADGE[m.invite_status]}`}>{m.invite_status}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Budget Split Editor — Lead only */}
        {isLead && (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Coins weight="fill" className="w-5 h-5 text-emerald-400" />Budget Split &amp; Scope</CardTitle>
              <p className="text-xs text-zinc-500">
                Allocate the budget across members. Shares must total 100% to save.{" "}
                <Link to="/coalitions/playbook" className="text-emerald-400 hover:text-emerald-300 underline" data-testid="budget-help-link">
                  <Question weight="fill" className="inline w-3 h-3 mr-0.5" />How does this work?
                </Link>
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {(coalition.members || []).filter(m => m.invite_status === "accepted").map(m => (
                <div key={m.user_id} className="grid grid-cols-1 sm:grid-cols-[1fr_100px] gap-2 p-3 bg-zinc-900/60 rounded-sm border border-zinc-800">
                  <div>
                    <p className="text-sm font-bold text-white">{m.org_name}</p>
                    <Input
                      value={splitDraft[m.user_id]?.scope_of_work || ""}
                      onChange={(e) => setSplitDraft({ ...splitDraft, [m.user_id]: { ...splitDraft[m.user_id], scope_of_work: e.target.value } })}
                      placeholder="Scope of work (what they deliver)"
                      title="What this partner specifically delivers. This goes into their Subgrant Agreement — be specific."
                      className="bg-zinc-950 border-zinc-800 mt-2 text-xs"
                      data-testid={`scope-${m.email}`}
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-zinc-500 uppercase tracking-wider" title="Each partner gets this % of the total grant. All shares must sum to 100% to save.">% Share</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={splitDraft[m.user_id]?.budget_share_pct ?? 0}
                      onChange={(e) => setSplitDraft({ ...splitDraft, [m.user_id]: { ...splitDraft[m.user_id], budget_share_pct: e.target.value } })}
                      title="Each partner gets this % of the total grant. All shares must sum to 100% to save."
                      className="bg-zinc-950 border-zinc-800 text-center font-mono"
                      data-testid={`pct-${m.email}`}
                    />
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2">
                <p className={`text-sm font-mono ${Math.abs(splitTotal - 100) < 0.5 ? "text-emerald-400" : "text-amber-400"}`}>Total: {splitTotal.toFixed(1)}% {Math.abs(splitTotal - 100) < 0.5 && <CheckCircle weight="fill" className="inline w-4 h-4 ml-1" />}</p>
                <Button onClick={saveSplits} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="save-splits-btn">Save Budget Split</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Vault */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><FileText weight="fill" className="w-5 h-5 text-emerald-400" />Document Vault</CardTitle>
            <p className="text-xs text-zinc-500">
              Templates auto-filled from coalition data. Print, sign, upload back when complete.{" "}
              <Link to="/coalitions/playbook" className="text-emerald-400 hover:text-emerald-300 underline" data-testid="docs-help-link">
                <Question weight="fill" className="inline w-3 h-3 mr-0.5" />Help me understand
              </Link>
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            <button onClick={() => openDoc(`/coalitions/${coalitionId}/mou`)} className="w-full flex items-center justify-between gap-3 p-3 bg-zinc-900/60 rounded-sm border border-zinc-800 hover:border-emerald-500/40 text-left" data-testid="open-mou-btn">
              <div>
                <p className="font-bold text-white text-sm">Memorandum of Understanding (MOU)</p>
                <p className="text-xs text-zinc-500">The umbrella agreement — signed by all members</p>
              </div>
              <FilePdf weight="duotone" className="w-6 h-6 text-emerald-400" />
            </button>
            <button onClick={() => openDoc(`/coalitions/${coalitionId}/checklist`)} className="w-full flex items-center justify-between gap-3 p-3 bg-zinc-900/60 rounded-sm border border-zinc-800 hover:border-emerald-500/40 text-left" data-testid="open-checklist-btn">
              <div>
                <p className="font-bold text-white text-sm flex items-center gap-1.5"><ListChecks weight="fill" className="w-4 h-4 text-emerald-400" />Coalition Setup Checklist</p>
                <p className="text-xs text-zinc-500">Print this — check off items as you complete each step</p>
              </div>
              <FilePdf weight="duotone" className="w-6 h-6 text-emerald-400" />
            </button>
            {acceptedPartners.map(p => (
              <button key={p.user_id} onClick={() => openDoc(`/coalitions/${coalitionId}/subgrant/${p.user_id}`)} className="w-full flex items-center justify-between gap-3 p-3 bg-zinc-900/60 rounded-sm border border-zinc-800 hover:border-emerald-500/40 text-left" data-testid={`open-subgrant-${p.email}`}>
                <div>
                  <p className="font-bold text-white text-sm">Subgrant Agreement — {p.org_name}</p>
                  <p className="text-xs text-zinc-500">{p.budget_share_pct.toFixed(0)}% share · {coalition.total_budget > 0 ? `$${(coalition.total_budget * p.budget_share_pct / 100).toLocaleString(undefined, {maximumFractionDigits: 0})}` : "TBD"}</p>
                </div>
                <FilePdf weight="duotone" className="w-6 h-6 text-emerald-400" />
              </button>
            ))}
            {acceptedPartners.length === 0 && (
              <p className="text-xs text-zinc-500 italic text-center py-2">Subgrant agreements will appear here as partners accept their invitations.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader><DialogTitle className="text-white">Invite a partner organization</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Partner email *</Label>
              <Input value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="partner@theirorg.com" className="bg-zinc-900 border-zinc-800" data-testid="invite-email" />
            </div>
            <div>
              <Label className="text-xs">Organization name (helps them recognize the invite)</Label>
              <Input value={inviteForm.org_name} onChange={(e) => setInviteForm({ ...inviteForm, org_name: e.target.value })} placeholder="Their Organization" className="bg-zinc-900 border-zinc-800" data-testid="invite-org" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button onClick={inviteSubmit} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="invite-submit">Send invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Coalitions;
